// client/src/components/WelcomePage.js
// First-time onboarding wizard shown after a new user verifies their email and logs in.
// Steps: Welcome → Profile (avatar + bio) → Collections → Appearance → Done
import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, Typography, Stepper, Step, StepLabel,
  TextField, Avatar, CircularProgress, ToggleButton,
  ToggleButtonGroup, Chip, Paper, LinearProgress
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import { jwtDecode } from 'jwt-decode';
import axiosInstance from '../axiosInstance';

// The 7 collection types the user can choose to use
const COLLECTION_TYPES = [
  { key: 'Games Collection',      label: 'Games',       emoji: '🎮' },
  { key: 'Console Collection',    label: 'Consoles',    emoji: '🕹️' },
  { key: 'LEGO Collection',       label: 'LEGO',        emoji: '🧱' },
  { key: 'Movie Collection',      label: 'Movies',      emoji: '🎬' },
  { key: 'Music Collection',      label: 'Music',       emoji: '🎵' },
  { key: 'Book Collection',       label: 'Books',       emoji: '📚' },
  { key: 'Board Game Collection',    label: 'Board Games',   emoji: '🎲' },
  { key: 'Trading Cards Collection', label: 'Trading Cards', emoji: '🃏' },
  { key: 'Manga Collection',         label: 'Manga',         emoji: '📖' },
  { key: 'Comics Collection',        label: 'Comics',        emoji: '💥' },
];

const THEMES = [
  { value: 'indigo',    label: 'Indigo' },
  { value: 'blue',      label: 'Blue' },
  { value: 'purple',    label: 'Purple' },
  { value: 'green',     label: 'Green' },
  { value: 'red',       label: 'Red' },
  { value: 'orange',    label: 'Orange' },
  { value: 'dark',      label: 'Dark' },
  { value: 'light',     label: 'Light' },
];

const STEPS = ['Welcome', 'Your Profile', 'Collections', 'Appearance', 'All Done'];

function WelcomePage() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const user = token ? jwtDecode(token) : null;
  const userId = user?.id;

  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);

  // Step 2 — Profile
  const [bio, setBio] = useState('');
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null);
  const fileInputRef = useRef(null);

  // Step 3 — Collections (all enabled by default — hiddenButtons lists the HIDDEN ones)
  const [hiddenCollections, setHiddenCollections] = useState([]);

  // Step 4 — Appearance
  const [theme, setTheme] = useState('indigo');

  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const handleAvatarChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
  };

  const toggleCollection = (key) => {
    setHiddenCollections(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const saveProfileStep = async () => {
    // Upload avatar if selected
    if (avatarFile && userId) {
      const formData = new FormData();
      formData.append('profileImage', avatarFile);
      await axiosInstance.post(`/users/${userId}/profile-image`, formData, {
        headers: { ...authHeader.headers, 'Content-Type': 'multipart/form-data' }
      }).catch(() => {}); // non-fatal
    }
    // Save bio
    if (bio.trim() && userId) {
      await axiosInstance.put(`/users/${userId}`, { bio: bio.trim() }, authHeader)
        .catch(() => {});
    }
  };

  const saveCollectionsStep = async () => {
    if (!userId) return;
    await axiosInstance.put(`/users/${userId}`, { hiddenButtons: hiddenCollections }, authHeader)
      .catch(() => {});
  };

  const saveAppearanceStep = async () => {
    if (!userId) return;
    await axiosInstance.put(`/users/${userId}`, {
      preferences: { theme }
    }, authHeader).catch(() => {});
  };

  const handleNext = async () => {
    setSaving(true);
    try {
      if (step === 1) await saveProfileStep();
      if (step === 2) await saveCollectionsStep();
      if (step === 3) await saveAppearanceStep();
    } catch (_) {
      // non-fatal — continue regardless
    } finally {
      setSaving(false);
    }
    setStep(s => s + 1);
  };

  const handleFinish = async () => {
    setSaving(true);
    try {
      const res = await axiosInstance.post('/auth/complete-onboarding', {}, authHeader);
      if (res.data?.token) {
        localStorage.setItem('token', res.data.token);
      }
    } catch (_) {
      // non-fatal
    } finally {
      setSaving(false);
    }
    window.location.replace('/');
  };

  const handleSkip = () => {
    if (step < STEPS.length - 2) {
      setStep(s => s + 1);
    } else {
      handleFinish();
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #3f51b5 0%, #7c4dff 100%)',
      p: 2
    }}>
      <Paper elevation={8} sx={{ width: '100%', maxWidth: 600, borderRadius: 3, overflow: 'hidden' }}>
        {/* Progress bar */}
        <LinearProgress
          variant="determinate"
          value={(step / (STEPS.length - 1)) * 100}
          sx={{ height: 4 }}
        />

        <Box sx={{ p: { xs: 3, sm: 5 } }}>
          {/* Stepper */}
          <Stepper activeStep={step} alternativeLabel sx={{ mb: 4 }}>
            {STEPS.map(label => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* ── Step 0: Welcome ── */}
          {step === 0 && (
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" fontWeight={700} gutterBottom>
                Welcome to InterCollect{user?.username ? `, ${user.username}` : ''}! 🦕
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                Let's take a moment to set up your profile so you can get the most out of your collections.
                This only takes about a minute.
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={() => setStep(1)}
                sx={{ px: 5, py: 1.5, borderRadius: 3 }}
              >
                Get Started
              </Button>
              <Box sx={{ mt: 2 }}>
                <Button variant="text" size="small" color="inherit" onClick={handleFinish}>
                  Skip setup and go to home
                </Button>
              </Box>
            </Box>
          )}

          {/* ── Step 1: Profile ── */}
          {step === 1 && (
            <Box>
              <Typography variant="h5" fontWeight={700} gutterBottom>Your Profile</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Add a photo and a short bio so others can get to know you.
              </Typography>

              {/* Avatar upload */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Avatar
                  src={avatarPreview || undefined}
                  sx={{ width: 80, height: 80, fontSize: 32, bgcolor: 'primary.main', cursor: 'pointer' }}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {!avatarPreview && <CameraAltIcon />}
                </Avatar>
                <Box>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {avatarPreview ? 'Change Photo' : 'Upload Photo'}
                  </Button>
                  {avatarPreview && (
                    <Button
                      size="small"
                      color="inherit"
                      onClick={() => { setAvatarPreview(null); setAvatarFile(null); }}
                      sx={{ ml: 1 }}
                    >
                      Remove
                    </Button>
                  )}
                  <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                    JPG or PNG, max 5 MB
                  </Typography>
                </Box>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  style={{ display: 'none' }}
                  onChange={handleAvatarChange}
                />
              </Box>

              <TextField
                label="Bio"
                multiline
                rows={3}
                fullWidth
                value={bio}
                onChange={e => setBio(e.target.value)}
                inputProps={{ maxLength: 200 }}
                placeholder="Tell the community a little about yourself…"
                helperText={`${bio.length}/200`}
              />
            </Box>
          )}

          {/* ── Step 2: Collections ── */}
          {step === 2 && (
            <Box>
              <Typography variant="h5" fontWeight={700} gutterBottom>Your Collections</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Choose which collection types you want to use. You can change this any time in your
                profile settings. All are enabled by default.
              </Typography>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                {COLLECTION_TYPES.map(c => {
                  const active = !hiddenCollections.includes(c.key);
                  return (
                    <Chip
                      key={c.key}
                      label={`${c.emoji} ${c.label}`}
                      onClick={() => toggleCollection(c.key)}
                      color={active ? 'primary' : 'default'}
                      variant={active ? 'filled' : 'outlined'}
                      sx={{ fontSize: '1rem', py: 2.5, px: 1, cursor: 'pointer' }}
                    />
                  );
                })}
              </Box>

              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                {hiddenCollections.length === 0
                  ? 'All collections enabled'
                  : `${COLLECTION_TYPES.length - hiddenCollections.length} of ${COLLECTION_TYPES.length} enabled`}
              </Typography>
            </Box>
          )}

          {/* ── Step 3: Appearance ── */}
          {step === 3 && (
            <Box>
              <Typography variant="h5" fontWeight={700} gutterBottom>Appearance</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Pick a colour theme. You can customise further (fonts, accent colours) from your profile
                settings later.
              </Typography>

              <ToggleButtonGroup
                value={theme}
                exclusive
                onChange={(_, val) => val && setTheme(val)}
                sx={{ flexWrap: 'wrap', gap: 1 }}
              >
                {THEMES.map(t => (
                  <ToggleButton
                    key={t.value}
                    value={t.value}
                    sx={{
                      minWidth: 90,
                      borderRadius: '8px !important',
                      border: '1px solid',
                      m: 0.5
                    }}
                  >
                    {t.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
          )}

          {/* ── Step 4: Done ── */}
          {step === 4 && (
            <Box sx={{ textAlign: 'center' }}>
              <CheckCircleOutlineIcon sx={{ fontSize: 72, color: 'success.main', mb: 2 }} />
              <Typography variant="h4" fontWeight={700} gutterBottom>
                You're all set!
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                Your profile is ready. Head to the home page to start adding to your collections.
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={handleFinish}
                disabled={saving}
                startIcon={saving ? <CircularProgress size={18} /> : null}
                sx={{ px: 5, py: 1.5, borderRadius: 3 }}
              >
                {saving ? 'Saving…' : 'Go to Dashboard'}
              </Button>
            </Box>
          )}

          {/* Navigation buttons — steps 1–3 */}
          {step > 0 && step < 4 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Button
                variant="outlined"
                onClick={() => setStep(s => s - 1)}
                disabled={saving}
              >
                Back
              </Button>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  color="inherit"
                  onClick={handleSkip}
                  disabled={saving}
                >
                  Skip
                </Button>
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={saving}
                  startIcon={saving ? <CircularProgress size={16} /> : null}
                >
                  {saving ? 'Saving…' : 'Next'}
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
}

export default WelcomePage;
